import { Injectable, NgZone, inject } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { BehaviorSubject, Subscription, filter, interval } from 'rxjs';

// Browser-specific messages for a failed dynamic import (Chrome, Safari, Firefox)
const CHUNK_LOAD_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;
const CHUNK_RELOAD_TIMESTAMP_KEY = 'rmb-chunk-reload-at';
const CHUNK_RELOAD_GUARD_MS = 10_000;

/**
 * Keeps open tabs in sync with deployed versions: offers updates found by the service worker
 * and reloads the page when the running version can no longer load its own files.
 */
@Injectable({
  providedIn: 'root',
})
export class VersionCheckerService {
  private swUpdate = inject(SwUpdate);
  private router = inject(Router);
  private zone = inject(NgZone);

  isNewVersionAvailable$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  intervalSource = interval(15 * 60 * 1000); // check every 15 mins for update
  intervalSubscription: Subscription;

  constructor() {
    this.reloadOnChunkLoadError();
    this.watchServiceWorker();
    this.checkForUpdate();
  }

  checkForUpdate(): void {
    this.intervalSubscription?.unsubscribe();
    if (!this.swUpdate.isEnabled) {
      return;
    }

    // A found update is reported through the VERSION_READY event once it is fully downloaded
    this.zone.runOutsideAngular(() => {
      this.intervalSubscription = this.intervalSource.subscribe(() => {
        this.swUpdate.checkForUpdate().catch((error) => console.error('Failed to check for updates:', error));
      });
    });
  }

  applyUpdate(): void {
    // Reload the page to update to the latest version after the new version is activated
    this.swUpdate
      .activateUpdate()
      .then(() => this.reloadPage())
      .catch((error) => console.error('Failed to apply updates:', error));
  }

  /**
   * Reloads the page, optionally navigating to the given app URL instead of the current one.
   * @param url router URL to load after the reload
   */
  reloadPage(url?: string): void {
    if (url) {
      document.location.assign(url);
    } else {
      document.location.reload();
    }
  }

  private watchServiceWorker(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
      .subscribe(() => this.isNewVersionAvailable$.next(true));

    // The cached version is broken (e.g. files were removed from the server), only a reload helps
    this.swUpdate.unrecoverable.subscribe((event) => {
      console.error('Service worker is in an unrecoverable state, reloading:', event.reason);
      this.reloadPage();
    });
  }

  private reloadOnChunkLoadError(): void {
    this.router.events
      .pipe(filter((event): event is NavigationError => event instanceof NavigationError && this.isChunkLoadError(event.error)))
      .subscribe((event) => {
        if (this.wasReloadedRecently()) {
          console.error('Failed to load module after reload:', event.error);
          return;
        }
        this.rememberReload();
        this.reloadPage(event.url);
      });
  }

  private isChunkLoadError(error: unknown): boolean {
    return error instanceof Error && CHUNK_LOAD_ERROR_PATTERN.test(error.message);
  }

  // Without sessionStorage (privacy settings) a reload could not be detected, so it is skipped to avoid a reload loop
  private wasReloadedRecently(): boolean {
    try {
      const reloadedAt = Number(sessionStorage.getItem(CHUNK_RELOAD_TIMESTAMP_KEY));
      return Date.now() - reloadedAt < CHUNK_RELOAD_GUARD_MS;
    } catch (error) {
      console.error('Failed to read reload timestamp:', error);
      return true;
    }
  }

  private rememberReload(): void {
    try {
      sessionStorage.setItem(CHUNK_RELOAD_TIMESTAMP_KEY, String(Date.now()));
    } catch (error) {
      console.error('Failed to store reload timestamp:', error);
    }
  }
}
